import { parseOrgScope, useAuthStore, authEvents } from './chunk-G2RM2HWO.js';
import { resolveContextBaseUrl, getActiveApp } from '@quickbee/api-config';

// src/internal/jwt.ts
function base64UrlDecode(segment) {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  if (typeof atob === "function") {
    return atob(base64);
  }
  return Buffer.from(base64, "base64").toString("utf-8");
}
function normaliseRoles(claim) {
  if (Array.isArray(claim)) return claim;
  if (typeof claim === "string" && claim.length > 0) return [claim];
  return [];
}
function normaliseClaims(raw) {
  const rolesClaim = raw["roles"] ?? raw["cognito:groups"];
  return {
    ...raw,
    userId: raw["userId"] ?? raw["sub"],
    tenantId: raw["tenantId"] ?? raw["tenant_id"],
    // Distinct from `userId`/`sub` — see `AuthUser.customerId`. Decoded but never
    // surfaced until now, which left customer-keyed screens passing the auth subject
    // where an account id was required.
    customerId: raw["customerId"] ?? raw["customer_id"],
    roles: normaliseRoles(rolesClaim),
    kycTier: raw["kycTier"] ?? raw["kyc_tier"] ?? "KYC0",
    // Cognito mints no session id; the backend's single-active-session check tolerates its
    // absence, and `X-Device-Session-Id` is then simply not sent.
    sessionId: raw["sessionId"] ?? raw["origin_jti"] ?? "",
    // Minted by PreTokenGenerationV2Handler from the `custom:org_scope` attribute
    // identity-service mirrors on every role assign/revoke. Absent on older tokens.
    orgScope: raw["orgScope"] ?? raw["org_scope"],
    exp: raw["exp"]
  };
}
function decodeAuthToken(token) {
  const parts = token.split(".");
  const payload = parts[1];
  if (parts.length !== 3 || !payload) return null;
  try {
    const json = base64UrlDecode(payload);
    return normaliseClaims(JSON.parse(json));
  } catch {
    return null;
  }
}
function isTokenExpired(claims, skewMs = 0) {
  if (!claims.exp) return true;
  return Date.now() >= claims.exp * 1e3 - skewMs;
}

// src/internal/tokenStorage.ts
var AUTH_SESSION_STORAGE_KEY = "qb_auth_session";
var STORAGE_VERSION = 1;
function readStoredSession() {
  const raw = safeGetItem(AUTH_SESSION_STORAGE_KEY);
  if (!raw) return null;
  return parseStoredSession(raw);
}
function parseStoredSession(raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed;
  if (candidate.version !== STORAGE_VERSION) return null;
  if (typeof candidate.accessToken !== "string" || !candidate.accessToken) return null;
  if (typeof candidate.expiresAt !== "number" || !Number.isFinite(candidate.expiresAt)) return null;
  return {
    version: STORAGE_VERSION,
    accessToken: candidate.accessToken,
    expiresAt: candidate.expiresAt,
    ...typeof candidate.refreshToken === "string" && candidate.refreshToken ? { refreshToken: candidate.refreshToken } : {},
    ...typeof candidate.refreshUsername === "string" && candidate.refreshUsername ? { refreshUsername: candidate.refreshUsername } : {},
    ...candidate.profile && typeof candidate.profile === "object" ? { profile: candidate.profile } : {}
  };
}
function writeStoredSession(tokens) {
  const previous = readStoredSession();
  const refreshToken = tokens.refreshToken ?? previous?.refreshToken;
  const refreshUsername = tokens.refreshUsername ?? previous?.refreshUsername;
  const profile = tokens.profile ?? previous?.profile;
  const record = {
    version: STORAGE_VERSION,
    accessToken: tokens.accessToken,
    expiresAt: tokens.expiresAt,
    ...refreshToken ? { refreshToken } : {},
    ...refreshUsername ? { refreshUsername } : {},
    ...profile ? { profile } : {}
  };
  safeSetItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(record));
}
function clearStoredSession() {
  safeRemoveItem(AUTH_SESSION_STORAGE_KEY);
}
var EXPIRY_LEAD_MS = 6e4;
function isStoredSessionUsable(session, now = Date.now()) {
  return !!session && session.expiresAt - EXPIRY_LEAD_MS > now;
}
function storedSessionToTokens(session) {
  return {
    accessToken: session.accessToken,
    expiresAt: session.expiresAt,
    ...session.refreshToken ? { refreshToken: session.refreshToken } : {},
    ...session.refreshUsername ? { refreshUsername: session.refreshUsername } : {},
    ...session.profile ? { profile: session.profile } : {}
  };
}
function subscribeToStoredSession(listener) {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") {
    return () => {
    };
  }
  const handler = (event) => {
    if (event.key !== null && event.key !== AUTH_SESSION_STORAGE_KEY) return;
    if (event.key === null) {
      listener(null);
      return;
    }
    listener(event.newValue ? parseStoredSession(event.newValue) : null);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
function safeGetItem(key) {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSetItem(key, value) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key, value);
  } catch {
  }
}
function safeRemoveItem(key) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(key);
  } catch {
  }
}

// src/internal/sessionClient.ts
var DEVICE_ID_STORAGE_KEY = "qb_device_id";
function getBrowserDeviceInfo() {
  let deviceId = null;
  try {
    if (typeof localStorage !== "undefined") {
      deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
      if (!deviceId) {
        deviceId = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `dev-${Math.abs(Date.now()).toString(36)}`;
        localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
      }
    }
  } catch {
  }
  return {
    deviceId: deviceId ?? `dev-${Math.abs(Date.now()).toString(36)}`,
    platform: "web",
    ...typeof navigator !== "undefined" && navigator.userAgent ? { userAgent: navigator.userAgent } : {}
  };
}
function newAuthRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `req-${Math.abs(Date.now()).toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}
function authUrl(options, path) {
  const base = (options.baseUrl ?? resolveContextBaseUrl("identity")).replace(/\/+$/, "");
  return `${base}/auth${path}`;
}
function authHeaders(options, { hasBody, idempotencyKey }) {
  const headers = {};
  if (hasBody) headers["Content-Type"] = "application/json";
  if (options.tenantId) headers["X-Tenant-ID"] = options.tenantId;
  if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;
  const appName = getActiveApp();
  if (appName) headers["X-App-Name"] = appName;
  return headers;
}
function toSessionTokens(payload) {
  if (!payload || typeof payload.accessToken !== "string" || !payload.accessToken) {
    return null;
  }
  const ttlMs = Number(payload.expiresInSeconds) * 1e3;
  return {
    accessToken: payload.accessToken,
    expiresAt: Date.now() + (Number.isFinite(ttlMs) ? ttlMs : 0),
    ...payload.profile ? { profile: payload.profile } : {},
    ...payload.refreshToken ? { refreshToken: payload.refreshToken } : {},
    ...payload.refreshUsername ? { refreshUsername: payload.refreshUsername } : {}
  };
}
function parseRetryAfterSeconds(value, now = Date.now()) {
  if (!value) return void 0;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return void 0;
  return Math.max(0, Math.ceil((at - now) / 1e3));
}
async function readFailure(res, fallback) {
  let code = "UNKNOWN_ERROR";
  let message = fallback;
  try {
    const envelope = await res.json();
    const first = envelope?.errors?.[0];
    if (first?.code) code = first.code;
    if (first?.message) message = first.message;
  } catch {
  }
  const retryAfterSeconds = parseRetryAfterSeconds(
    typeof res.headers?.get === "function" ? res.headers.get("Retry-After") : void 0
  );
  return {
    status: res.status,
    code,
    message,
    ...retryAfterSeconds !== void 0 ? { retryAfterSeconds } : {}
  };
}
async function readTokens(res) {
  if (!res.ok) return null;
  try {
    const envelope = await res.json();
    return toSessionTokens(envelope?.data);
  } catch {
    return null;
  }
}
async function readAccepted() {
  return true;
}
async function postJson(options, path, body, idempotencyKey, fallbackMessage, readOk) {
  const fetchImpl = options.fetchImpl ?? fetch;
  let res;
  try {
    res = await fetchImpl(authUrl(options, path), {
      method: "POST",
      credentials: "include",
      headers: authHeaders(options, { hasBody: true, idempotencyKey }),
      body: JSON.stringify(body)
    });
  } catch {
    return {
      ok: false,
      error: {
        status: 0,
        code: "NETWORK_ERROR",
        message: "Could not reach the server. Check your connection and try again."
      }
    };
  }
  if (!res.ok) {
    return { ok: false, error: await readFailure(res, fallbackMessage) };
  }
  const data = await readOk(res);
  if (data === null) {
    return {
      ok: false,
      error: { status: res.status, code: "MALFORMED_RESPONSE", message: fallbackMessage }
    };
  }
  return { ok: true, data };
}
async function refreshSession(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const stored = readStoredSession();
  const body = stored?.refreshToken ? {
    refreshToken: stored.refreshToken,
    ...stored.refreshUsername ? { refreshUsername: stored.refreshUsername } : {}
  } : null;
  let res;
  try {
    res = await fetchImpl(authUrl(options, "/refresh"), {
      method: "POST",
      credentials: "include",
      headers: authHeaders(options, body ? { hasBody: true } : {}),
      ...body ? { body: JSON.stringify(body) } : {}
    });
  } catch {
    return { ok: false, failure: { status: 0, transient: true } };
  }
  if (!res.ok) {
    return { ok: false, failure: { status: res.status ?? 0, transient: isTransientStatus(res.status) } };
  }
  const tokens = await readTokens(res);
  if (!tokens) {
    return { ok: false, failure: { status: res.status, transient: true } };
  }
  return { ok: true, tokens };
}
function isTransientStatus(status) {
  if (typeof status !== "number") return false;
  return status >= 500 || status === 429 || status === 408;
}
async function verifyOtp(options, request) {
  return postJson(
    options,
    "/verify-otp",
    // Backend `VerifyOtpRequest` fields: phone, otp, deviceId, platform, userAgent.
    {
      phone: request.phone,
      otp: request.code,
      deviceId: request.device.deviceId,
      platform: request.device.platform,
      ...request.device.userAgent ? { userAgent: request.device.userAgent } : {}
    },
    `auth-verify-otp:${request.requestId}`,
    "Invalid or expired OTP. Please try again.",
    readTokens
  );
}
async function passwordLogin(options, request) {
  return postJson(
    options,
    "/password-login",
    // Backend `PasswordLoginRequest` fields: username, password, deviceId, platform, userAgent.
    {
      username: request.username,
      password: request.password,
      deviceId: request.device.deviceId,
      platform: request.device.platform,
      ...request.device.userAgent ? { userAgent: request.device.userAgent } : {}
    },
    `auth-password-login:${request.requestId}`,
    "Invalid mobile number/email or password.",
    readTokens
  );
}
async function requestOtp(options, request) {
  return postJson(
    options,
    "/login",
    { phone: request.phone },
    `auth-login:${request.requestId}`,
    "Could not send the OTP. Please try again.",
    readAccepted
  );
}
async function resendOtp(options, request) {
  return postJson(
    options,
    "/otp/resend",
    { phone: request.phone },
    `auth-otp-resend:${request.requestId}`,
    "Could not resend the OTP. Please try again.",
    readAccepted
  );
}
async function registerSaver(options, request) {
  return postJson(
    options,
    "/register",
    // Backend `RegisterSaverRequest` fields: phone (E.164), fullName, email, agentCode,
    // termsAccepted, marketingOptIn. `email` is `@Email`-validated but not `@NotBlank`, so
    // an absent address is legal — an empty string is not. Same for `agentCode`
    // (`@Pattern`, optional).
    {
      phone: request.phone,
      fullName: request.fullName,
      ...request.email?.trim() ? { email: request.email.trim() } : {},
      ...request.agentCode?.trim() ? { agentCode: request.agentCode.trim().toUpperCase() } : {},
      termsAccepted: request.termsAccepted,
      ...request.marketingOptIn !== void 0 ? { marketingOptIn: request.marketingOptIn } : {}
    },
    `auth-register:${request.requestId}`,
    "Could not start registration. Please try again.",
    readAccepted
  );
}
async function logoutSession(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  try {
    await fetchImpl(authUrl(options, "/logout"), {
      method: "POST",
      credentials: "include",
      headers: {
        ...authHeaders(options, { idempotencyKey: `auth-logout:${newAuthRequestId()}` }),
        // The only authenticated call on this client — see `SessionClientOptions.accessToken`.
        ...options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}
      }
    });
  } catch {
  }
}

// src/internal/refreshCoordinator.ts
function createRefreshCoordinator() {
  let inFlight = null;
  function attemptRefreshDetailed(options = {}) {
    if (!inFlight) {
      inFlight = performRefresh(options).finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  }
  return {
    attemptRefreshDetailed,
    attemptRefresh: (options) => attemptRefreshDetailed(options).then((result) => result.tokens)
  };
}
function resolveTenantId(options, store) {
  if (options.tenantId) return options.tenantId;
  const state = store.getState();
  const fromStore = state.tenantConfig?.tenantId ?? state.user?.tenantId;
  if (fromStore) return fromStore;
  const stored = readStoredSession();
  if (!stored) return void 0;
  return decodeAuthToken(stored.accessToken)?.tenantId ?? void 0;
}
function currentTokenExpiry(store) {
  const stored = readStoredSession();
  if (stored) return stored.expiresAt;
  const accessToken = store.getState().accessToken;
  if (!accessToken) return 0;
  const claims = decodeAuthToken(accessToken);
  return claims?.exp ? claims.exp * 1e3 : 0;
}
async function performRefresh(options) {
  const store = options.authStore ?? useAuthStore;
  const tenantId = resolveTenantId(options, store);
  const outcome = await refreshSession({
    ...options.baseUrl !== void 0 ? { baseUrl: options.baseUrl } : {},
    ...options.fetchImpl ? { fetchImpl: options.fetchImpl } : {},
    ...tenantId ? { tenantId } : {}
  });
  if (!outcome.ok) {
    const { failure } = outcome;
    const survivable = failure.transient && currentTokenExpiry(store) > Date.now();
    if (survivable) {
      return { tokens: null, failure, terminated: false };
    }
    clearStoredSession();
    store.getState().clearAuth();
    authEvents.emit("sessionExpired", { reason: "refresh-failed" });
    options.onAuthFailure?.();
    return { tokens: null, failure, terminated: true };
  }
  writeStoredSession(outcome.tokens);
  store.getState().setAccessToken(outcome.tokens.accessToken);
  authEvents.emit("tokenRefreshed", { expiresAt: outcome.tokens.expiresAt });
  return { tokens: outcome.tokens, terminated: false };
}
var sharedRefreshCoordinator = createRefreshCoordinator();

// src/internal/tokenRefresh.ts
var TOKEN_REFRESH_LEAD_MS = 6e4;
var TRANSIENT_RETRY_DELAYS_MS = [5e3, 15e3, 3e4, 6e4];
function scheduleTokenRefresh(expiresAt, clientOptions = {}) {
  let timerId;
  let stopped = false;
  let running = false;
  let currentExpiresAt = expiresAt;
  let transientAttempts = 0;
  const attemptOptions = {
    ...clientOptions.baseUrl !== void 0 ? { baseUrl: clientOptions.baseUrl } : {},
    ...clientOptions.fetchImpl ? { fetchImpl: clientOptions.fetchImpl } : {},
    // The header whose absence used to end every session. Still passed explicitly —
    // the coordinator can derive it, but a caller that knows the tenant is authoritative.
    ...clientOptions.tenantId ? { tenantId: clientOptions.tenantId } : {}
  };
  const scheduleIn = (delay) => {
    if (stopped) return;
    clearTimeout(timerId);
    timerId = setTimeout(() => void refresh(), Math.max(0, delay));
  };
  const scheduleForExpiry = () => scheduleIn(currentExpiresAt - Date.now() - TOKEN_REFRESH_LEAD_MS);
  async function refresh() {
    if (stopped || running) return;
    running = true;
    try {
      const result = await sharedRefreshCoordinator.attemptRefreshDetailed(attemptOptions);
      if (stopped) return;
      if (result.tokens) {
        transientAttempts = 0;
        currentExpiresAt = result.tokens.expiresAt;
        scheduleForExpiry();
        return;
      }
      if (result.terminated) {
        stopped = true;
        return;
      }
      const delay = TRANSIENT_RETRY_DELAYS_MS[Math.min(transientAttempts, TRANSIENT_RETRY_DELAYS_MS.length - 1)] ?? 6e4;
      transientAttempts += 1;
      scheduleIn(delay);
    } finally {
      running = false;
    }
  }
  const refreshIfDue = () => {
    if (stopped) return;
    if (Date.now() >= currentExpiresAt - TOKEN_REFRESH_LEAD_MS) void refresh();
  };
  const detachWakeListeners = attachWakeListeners(refreshIfDue);
  scheduleForExpiry();
  return {
    stop: () => {
      stopped = true;
      clearTimeout(timerId);
      detachWakeListeners();
    }
  };
}
function attachWakeListeners(onWake) {
  const teardown = [];
  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    const onVisible = () => {
      if (document.visibilityState === "visible") onWake();
    };
    document.addEventListener("visibilitychange", onVisible);
    teardown.push(() => document.removeEventListener("visibilitychange", onVisible));
  }
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("online", onWake);
    window.addEventListener("focus", onWake);
    teardown.push(() => {
      window.removeEventListener("online", onWake);
      window.removeEventListener("focus", onWake);
    });
  }
  return () => teardown.forEach((fn) => fn());
}

// src/internal/idleTimeout.ts
var IDLE_ACTIVITY_STORAGE_KEY = "qb_last_activity";
var DEFAULT_IDLE_TIMEOUT_MS = 30 * 6e4;
var DEFAULT_IDLE_WARNING_MS = 2 * 6e4;
var TICK_INTERVAL_MS = 1e3;
var ACTIVITY_WRITE_THROTTLE_MS = 5e3;
var ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
  "scroll"
];
var INERT = {
  stop: () => {
  },
  recordActivity: () => {
  },
  msUntilTimeout: () => Number.POSITIVE_INFINITY
};
function readSharedActivity() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(IDLE_ACTIVITY_STORAGE_KEY);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}
function writeSharedActivity(at) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(IDLE_ACTIVITY_STORAGE_KEY, String(at));
  } catch {
  }
}
function clearIdleActivity() {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(IDLE_ACTIVITY_STORAGE_KEY);
  } catch {
  }
}
function startIdleTimeout(config = {}) {
  const timeoutMs = config.timeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
  const enabled = config.enabled ?? true;
  if (!enabled || timeoutMs <= 0) return INERT;
  if (typeof document === "undefined" || typeof document.addEventListener !== "function") {
    return INERT;
  }
  const requestedWarning = config.warningMs ?? DEFAULT_IDLE_WARNING_MS;
  const warningMs = Math.max(0, Math.min(requestedWarning, Math.floor(timeoutMs / 2)));
  let lastActivity = readSharedActivity() ?? Date.now();
  let lastWrite = 0;
  let warned = false;
  let finished = false;
  const recordActivity = (now = Date.now()) => {
    if (finished) return;
    lastActivity = now;
    warned = false;
    if (now - lastWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastWrite = now;
      writeSharedActivity(now);
    }
  };
  const onActivity = () => recordActivity();
  const onVisibility = () => {
    if (document.visibilityState === "visible") recordActivity();
  };
  const onStorage = (event) => {
    if (event.key !== IDLE_ACTIVITY_STORAGE_KEY || !event.newValue) return;
    const shared = Number(event.newValue);
    if (Number.isFinite(shared) && shared > lastActivity) {
      lastActivity = shared;
      warned = false;
    }
  };
  ACTIVITY_EVENTS.forEach(
    (name) => document.addEventListener(name, onActivity, { passive: true, capture: true })
  );
  document.addEventListener("visibilitychange", onVisibility);
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("storage", onStorage);
  }
  const tick = () => {
    if (finished) return;
    const shared = readSharedActivity();
    if (shared !== null && shared > lastActivity) {
      lastActivity = shared;
      warned = false;
    }
    const idleFor = Date.now() - lastActivity;
    if (idleFor >= timeoutMs) {
      finished = true;
      stop();
      authEvents.emit("sessionIdleTimeout", { idleMs: idleFor });
      config.onTimeout?.();
      return;
    }
    if (!warned && warningMs > 0 && idleFor >= timeoutMs - warningMs) {
      warned = true;
      authEvents.emit("sessionIdleWarning", { msRemaining: timeoutMs - idleFor });
    }
  };
  const intervalId = setInterval(tick, TICK_INTERVAL_MS);
  function stop() {
    clearInterval(intervalId);
    ACTIVITY_EVENTS.forEach(
      (name) => document.removeEventListener(name, onActivity, { capture: true })
    );
    document.removeEventListener("visibilitychange", onVisibility);
    if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      window.removeEventListener("storage", onStorage);
    }
  }
  recordActivity();
  return {
    stop: () => {
      finished = true;
      stop();
    },
    recordActivity: () => recordActivity(),
    msUntilTimeout: () => Math.max(0, timeoutMs - (Date.now() - lastActivity))
  };
}

// src/internal/session.ts
var idlePolicy = {};
var idleHandle = null;
function restartIdleTracking(clientOptions) {
  idleHandle?.stop();
  const handle = startIdleTimeout({
    ...idlePolicy,
    onTimeout: () => {
      idlePolicy.onTimeout?.();
      void endAuthSession(clientOptions);
    }
  });
  idleHandle = handle;
  return () => {
    if (idleHandle !== handle) return;
    handle.stop();
    idleHandle = null;
  };
}
function stopIdleTracking() {
  idleHandle?.stop();
  idleHandle = null;
}
function recordIdleActivity() {
  idleHandle?.recordActivity();
}
function msUntilIdleTimeout() {
  return idleHandle?.msUntilTimeout() ?? Number.POSITIVE_INFINITY;
}
function buildAuthUser(tokens) {
  const claims = decodeAuthToken(tokens.accessToken);
  if (!claims || !claims.userId || !claims.tenantId) return null;
  const user = {
    userId: claims.userId,
    tenantId: claims.tenantId,
    roles: claims.roles ?? [],
    kycTier: claims.kycTier ?? "KYC0",
    sessionId: claims.sessionId ?? "",
    csrfToken: claims.csrfToken ?? "",
    ...claims.customerId ? { customerId: claims.customerId } : {},
    // Only set when the token actually carries the claim. An empty object here would
    // be indistinguishable from "no roles pinned" — which is the same answer — but
    // leaving the key off keeps `branchScopes === undefined` meaning "this token
    // predates org_scope", which is what the absence rule is about.
    ...claims.orgScope ? { branchScopes: parseOrgScope(claims.orgScope) } : {},
    ...tokens.profile?.fullName ? { name: tokens.profile.fullName } : {},
    ...tokens.profile?.phoneMasked ? { phone: tokens.profile.phoneMasked } : {}
  };
  return user;
}
function withPersistedProfile(tokens) {
  if (tokens.profile) return tokens;
  const storedProfile = readStoredSession()?.profile;
  return storedProfile ? { ...tokens, profile: storedProfile } : tokens;
}
function establishSession(tokens, clientOptions) {
  const enriched = withPersistedProfile(tokens);
  const user = buildAuthUser(enriched);
  if (!user) return null;
  writeStoredSession(enriched);
  const store = useAuthStore.getState();
  store.setSession({ user, accessToken: enriched.accessToken });
  const refreshHandle = scheduleTokenRefresh(enriched.expiresAt, clientOptions);
  const disposeIdle = restartIdleTracking(clientOptions);
  authEvents.emit("authStateChanged", { user, isAuthenticated: true });
  return {
    dispose: () => {
      refreshHandle.stop();
      disposeIdle();
    }
  };
}
async function initAuthSession(clientOptions = {}, options = {}) {
  idlePolicy = options.idle ?? {};
  const store = useAuthStore.getState();
  store.setLoading(true);
  const stored = readStoredSession();
  let rehydrated = null;
  if (isStoredSessionUsable(stored)) {
    rehydrated = establishSession(storedSessionToTokens(stored), clientOptions);
  }
  const outcome = await refreshSession(clientOptions);
  const established = outcome.ok ? establishSession(outcome.tokens, clientOptions) : null;
  if (established) {
    rehydrated?.dispose();
    return withCrossTabSync(established, clientOptions);
  }
  if (rehydrated) {
    return withCrossTabSync(rehydrated, clientOptions);
  }
  clearStoredSession();
  clearIdleActivity();
  stopIdleTracking();
  useAuthStore.getState().clearAuth();
  authEvents.emit("authStateChanged", { user: null, isAuthenticated: false });
  return withCrossTabSync({ dispose: () => {
  } }, clientOptions);
}
function withCrossTabSync(result, clientOptions) {
  let current = result;
  const unsubscribe = subscribeToStoredSession((session) => {
    current.dispose();
    current = { dispose: () => {
    } };
    if (!isStoredSessionUsable(session)) {
      if (useAuthStore.getState().isAuthenticated) {
        stopIdleTracking();
        useAuthStore.getState().clearAuth();
        authEvents.emit("authStateChanged", { user: null, isAuthenticated: false });
      }
      return;
    }
    const user = buildAuthUser(storedSessionToTokens(session));
    if (!user) return;
    useAuthStore.getState().setSession({ user, accessToken: session.accessToken });
    const refreshHandle = scheduleTokenRefresh(session.expiresAt, clientOptions);
    const disposeIdle = restartIdleTracking(clientOptions);
    current = {
      dispose: () => {
        refreshHandle.stop();
        disposeIdle();
      }
    };
    authEvents.emit("authStateChanged", { user, isAuthenticated: true });
  });
  return {
    dispose: () => {
      unsubscribe();
      current.dispose();
    }
  };
}
async function endAuthSession(clientOptions = {}) {
  const accessToken = useAuthStore.getState().accessToken;
  stopIdleTracking();
  clearIdleActivity();
  await logoutSession(accessToken ? { ...clientOptions, accessToken } : clientOptions);
  clearStoredSession();
  useAuthStore.getState().clearAuth();
  authEvents.emit("authStateChanged", { user: null, isAuthenticated: false });
}

export { AUTH_SESSION_STORAGE_KEY, DEFAULT_IDLE_TIMEOUT_MS, DEFAULT_IDLE_WARNING_MS, IDLE_ACTIVITY_STORAGE_KEY, buildAuthUser, clearIdleActivity, clearStoredSession, createRefreshCoordinator, decodeAuthToken, endAuthSession, establishSession, getBrowserDeviceInfo, initAuthSession, isStoredSessionUsable, isTokenExpired, logoutSession, msUntilIdleTimeout, newAuthRequestId, parseRetryAfterSeconds, passwordLogin, readStoredSession, recordIdleActivity, refreshSession, registerSaver, requestOtp, resendOtp, scheduleTokenRefresh, sharedRefreshCoordinator, startIdleTimeout, verifyOtp, writeStoredSession };
//# sourceMappingURL=internal.js.map
//# sourceMappingURL=internal.js.map